/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from 'react'
import ReactHtmlParser from 'react-html-parser'
import { useStore } from 'react-redux'
import { StatusSection } from '../../../../../components'
import { COUNTRY, COUNTRY_LABELS } from '../../../../../utils/constants'
import { TableViewPrint } from '../../../common/TableView'
import {
  FOUNDATION_TYPE,
  GEOMETRY_TYPE,
  HOLDOWN_SUBTYPE,
  HOLDOWN_SUBTYPE_LABEL,
  HOLDOWN_LABELS,
  LOAD_TYPE_CANADA,
  MATERIAL_TYPE,
  POST_BASE_LABELS,
  POST_BASE_MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
  POST_BASE_SUBTYPE_LABEL,
  UNITS,
  anchorageOptions,
  compressiveStrengthOptions,
  geometrySubTypeOptions,
  geometryTypeOptions,
  holdownLumberSpeciesOptions,
  materialTypeOptions,
  postBaseFastenersTypeOptions,
  postBaseLumberSpeciesOptions,
  postBaseMaterialTypeOptions,
  HOLDOWN_TYPE,
} from '../../../common/constants'
import {
  geometryTypeImages,
  getMinStemwallThicknessOptions,
  getPostThicknessOptions,
  getPostWidthOptions,
  getTextFromOptions,
} from '../../../common/fns'
import {
  corrosionResistanceOptions,
  downloadDurationData,
  foundationTypeData,
  holdownFastenersTypeData,
  installOverSheathingData,
  installationTypeData,
  loadTypeData,
  postBaseGeometrySubTypeData,
  postBaseGeometryTypeData,
  postBaseLoadTypeData,
  postTypeData,
  upliftDurationData,
  wallThicknessData,
  raisedFloorData,
} from '../../../common/json/staticData.json'
import { getTableHeader } from '../../../common/tableHeaders'
import { selectInputState } from '../../../store/slices/input.slice'
import {
  selectOutputDataFootNotes,
  selectOutputDataSummary,
  selectOutputState,
  selectOutputStatus,
  selectOutputVirtualizedData,
} from '../../../store/slices/output.slice'

const PrintableInputOutput = () => {
  const state = useStore().getState()
  const input = selectInputState(state)
  const { searchValue, sortBy } = selectOutputState(state)
  const status = selectOutputStatus(state)
  const summary = selectOutputDataSummary(state)
  const footNotes = selectOutputDataFootNotes(state)
  const virtualizedData = selectOutputVirtualizedData(state)

  const { postBaseDesign, holdownDesign, foundationDesign, settings } = input
  const { country } = settings
  const { summaryHeader, defaultSorted } = getTableHeader(input, summary)

  const renderedFootNotes = footNotes
    ?.filter((tag) => tag.startsWith('<li>')) // prevent render unexpected tags
    .join('')
    .replace(/\\/g, '')

  const outputData = virtualizedData || summary || []

  return (
    <>
      <h2>Input</h2>

      <ConditionPrintTable input={input} />
      <JobSettingsPrintTable input={input} />
      {holdownDesign && <HoldownDesignCriteriaPrintTable input={input} />}
      {postBaseDesign && (
        <>
          <PostBaseDesignCriteriaPrintTable input={input} />
          {country === COUNTRY.CANADA && <PostBaseFoundationPrintTable input={input} />}
        </>
      )}
      {foundationDesign && (
        <>
          <AnchorageDesignCriteriaPrintTable input={input} />
          <div className="page-break" />
          <GeometryTypeImages input={input} />
        </>
      )}

      <div className="page-break" />

      <h2>Output</h2>
      <section id="pfdOutputSummaryTable">
        <h4>Summary</h4>
        <div className="d-flex mt-3">{searchValue && <div> Search: {searchValue}</div>}</div>
      </section>

      {status.msgBody ? (
        <StatusSection
          statusLevel={status.msgLevel}
          statusIcon={'assets/images/icons/sst-icon-info.svg'}
          statusHeader={status.msgHeader}
          statusBody={status.msgBody}
        />
      ) : (
        <TableViewPrint
          columns={summaryHeader}
          data={outputData}
          sortBy={sortBy || defaultSorted}
        />
      )}

      {renderedFootNotes && (
        <>
          <h4 className="tableNotes">Table Notes</h4>
          <ol className="tableNotes">{ReactHtmlParser(renderedFootNotes)}</ol>
        </>
      )}
    </>
  )
}

const ConditionPrintTable = ({ input }) => {
  const columns = [
    {
      accessor: 'settings.country',
      Header: 'Country',
      Cell: ({ value }) => COUNTRY_LABELS[value],
    },
    {
      Header: 'Condition',
      Cell: ({
        row: {
          original: {
            postBaseDesign,
            holdownDesign,
            foundationDesign,
            settings: { country },
          },
        },
      }) =>
        [
          postBaseDesign && POST_BASE_LABELS[country],
          holdownDesign && HOLDOWN_LABELS[country],
          foundationDesign && 'Anchorage',
        ]
          .filter(Boolean)
          .join(', '),
    },
  ]

  return (
    <>
      <h4>Settings</h4>
      <TableViewPrint columns={columns} data={[input]} sortBy={[]} />
    </>
  )
}

const JobSettingsPrintTable = ({ input }) => {
  const {
    postBaseDesign,
    postBaseSubType,
    foundationDesign,
    holdownDesign,
    holdownType,
    foundationType,
    postBaseMaterialType,
    settings: { country },
  } = input
  const columns = {
    [COUNTRY.USA]: [
      {
        accessor: 'upliftLoadASD',
        Header: 'Uplift Load (160)(ASD)',
      },
      postBaseDesign &&
        (postBaseSubType !== POST_BASE_SUBTYPE.POST_INSTALLED ||
          postBaseMaterialType !== POST_BASE_MATERIAL_TYPE.WOOD) && {
          accessor: 'downloadASD',
          Header: 'Download (100)(ASD) ',
        },
      postBaseDesign && {
        accessor: 'lateralF1',
        Header: 'F1 (160)(ASD)',
      },
      postBaseDesign &&
        (postBaseSubType !== POST_BASE_SUBTYPE.POST_INSTALLED ||
          postBaseMaterialType !== POST_BASE_MATERIAL_TYPE.WOOD) && {
          accessor: 'lateralF2',
          Header: 'F2 (160)(ASD)',
        },
      postBaseDesign &&
        postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && {
          accessor: 'postBaseLoadType',
          Header: 'Load Type',
          Cell: ({ value }) => getTextFromOptions(postBaseLoadTypeData, value),
        },
      foundationDesign && {
        accessor: 'loadType',
        Header: 'Load Type',
        Cell: ({ value }) => getTextFromOptions(loadTypeData, value),
      },
    ],
    [COUNTRY.CANADA]: [
      {
        accessor: 'upliftDuration',
        Header: 'Uplift Duration',
        Cell: ({ value }) => getTextFromOptions(upliftDurationData, value),
      },
      holdownDesign &&
        holdownType === HOLDOWN_TYPE.CAST_IN_PLACE && {
          accessor: 'loadType',
          Header: 'Load Type',
          Cell: ({ value }) => getTextFromOptions(LOAD_TYPE_CANADA, value),
        },
      {
        accessor: 'factoredUpliftLRFD',
        Header: `Factored Uplift (${UNITS.LSD})`,
        Cell: ({ value }) => `${value} (lbs)`,
      },
      postBaseDesign &&
        foundationType !== FOUNDATION_TYPE.WOOD && {
          accessor: 'downloadDuration',
          Header: 'Download Duration',
          Cell: ({ value }) => getTextFromOptions(downloadDurationData, value),
        },
      postBaseDesign &&
        foundationType !== FOUNDATION_TYPE.WOOD && {
          accessor: 'factoredDownloadLRFD',
          Header: `Factored Download (${UNITS.LSD})`,
          Cell: ({ value }) => `${value} (lbs)`,
        },
      postBaseDesign && {
        accessor: 'lateralF1',
        Header: 'F1',
        Cell: ({ value }) => `${value} (lbs)`,
      },
      postBaseDesign &&
        foundationType !== FOUNDATION_TYPE.WOOD && {
          accessor: 'lateralF2',
          Header: 'F2',
          Cell: ({ value }) => `${value} (lbs)`,
        },
      {
        accessor: 'corrosionResistance',
        Header: 'Corrosion Resistance',
        Cell: ({ value }) =>
          value.map((val) => getTextFromOptions(corrosionResistanceOptions, val)).join(', '),
      },
      holdownDesign && {
        accessor: 'minStemwallThickness',
        Header: 'Stemwall Thickness',
        Cell: ({ value }) =>
          getTextFromOptions(getMinStemwallThicknessOptions(holdownDesign, foundationType), value),
      },
    ],
  }

  return (
    <>
      <h4>Job Settings</h4>
      <TableViewPrint columns={columns[country]?.filter(Boolean)} data={[input]} sortBy={[]} />
    </>
  )
}

const HoldownDesignCriteriaPrintTable = ({ input }) => {
  const {
    holdownDesign,
    holdownSubType,
    holdownType,
    lumberSpecies,
    settings: { country },
  } = input
  const columns = {
    [COUNTRY.USA]: [
      {
        accessor: 'holdownSubType',
        Header: 'Holdown Subtype',
        Cell: ({ value }) => HOLDOWN_SUBTYPE_LABEL[value],
      },
      {
        accessor: 'holdownFastenersType',
        Header: 'Post Fasteners',
        Cell: ({ value }) =>
          value.map((val) => getTextFromOptions(holdownFastenersTypeData, val)).join(', '),
      },
      {
        accessor: 'lumberSpecies',
        Header: 'Lumber Species',
        Cell: ({ value }) => getTextFromOptions(holdownLumberSpeciesOptions[country], value),
      },
      {
        accessor: 'wallThickness',
        Header: 'Stemwall Thickness',
        Cell: ({ value }) => getTextFromOptions(wallThicknessData, value),
      },
      holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP && {
        accessor: 'installOverSheathing',
        Header: 'Install Over 1/2" Sheathing',
        Cell: ({ value }) => getTextFromOptions(installOverSheathingData, value),
      },
    ],
    [COUNTRY.CANADA]: [
      {
        accessor: 'lumberSpecies',
        Header: 'Lumber Species',
        Cell: ({ value }) =>
          getTextFromOptions(holdownLumberSpeciesOptions[COUNTRY.CANADA][holdownType], value),
      },
      {
        accessor: 'postThickness',
        Header: 'Post Thickness',
        Cell: ({ value }) =>
          getTextFromOptions(getPostThicknessOptions(holdownDesign, lumberSpecies), value),
      },
      {
        accessor: 'postWidth',
        Header: 'Post Width',
        Cell: ({ value }) =>
          getTextFromOptions(getPostWidthOptions(holdownDesign, lumberSpecies), value),
      },
      {
        accessor: 'holdownType',
        Header: 'Holdown Type',
        Cell: ({ value }) => getTextFromOptions(postTypeData, value),
      },
      {
        accessor: 'holdownFastenersType',
        Header: 'Post Fasteners',
        Cell: ({ value }) =>
          value.map((val) => getTextFromOptions(holdownFastenersTypeData, val)).join(', '),
      },
      holdownType === HOLDOWN_TYPE.CAST_IN_PLACE && {
        accessor: 'installationType',
        Header: 'Installation Location',
        Cell: ({ value }) => getTextFromOptions(installationTypeData, value),
      },
    ],
  }

  return (
    <>
      <h4>Post & Holdown</h4>
      <TableViewPrint columns={columns[country]?.filter(Boolean)} data={[input]} sortBy={[]} />
    </>
  )
}

const PostBaseDesignCriteriaPrintTable = ({ input }) => {
  const {
    holdownDesign,
    postBaseSubType,
    postBaseType,
    postBaseLumberSpecies,
    settings: { country },
  } = input
  const columns = {
    [COUNTRY.USA]: [
      {
        accessor: 'postBaseSubType',
        Header: 'Post Base Subtype',
        Cell: ({ value }) => POST_BASE_SUBTYPE_LABEL[value],
      },
      {
        accessor: 'postBaseFastenersType',
        Header: 'Post Fasteners',
        Cell: ({ value }) =>
          value
            .map((val) => getTextFromOptions(postBaseFastenersTypeOptions[postBaseSubType], val))
            .join(', '),
      },
      {
        accessor: 'postBaseLumberSpecies',
        Header: 'Lumber Species',
        Cell: ({ value, row }) =>
          getTextFromOptions(
            postBaseLumberSpeciesOptions[country][row.original.postBaseSubType],
            value,
          ),
      },
      {
        accessor: 'postBaseMaterialType',
        Header: 'Base Material',
        Cell: ({ value, row }) =>
          getTextFromOptions(postBaseMaterialTypeOptions[row.original.postBaseSubType], value),
      },
      postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && {
        accessor: 'postBaseGeometryType',
        Header: 'Foundation Type',
        Cell: ({ value }) => getTextFromOptions(postBaseGeometryTypeData, value),
      },
      postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && {
        accessor: 'postBaseGeometrySubType',
        Header: 'Application',
        Cell: ({ value }) => getTextFromOptions(postBaseGeometrySubTypeData, value),
      },
      {
        accessor: 'corrosionResistance',
        Header: 'Corrosion Resistance',
        Cell: ({ value }) =>
          value.map((val) => getTextFromOptions(corrosionResistanceOptions, val)).join(', '),
      },
    ],
    [COUNTRY.CANADA]: [
      {
        accessor: 'postBaseLumberSpecies',
        Header: 'Lumber Species',
        Cell: ({ value }) =>
          getTextFromOptions(postBaseLumberSpeciesOptions[COUNTRY.CANADA][postBaseType], value),
      },
      {
        accessor: 'postThickness',
        Header: 'Post Thickness',
        Cell: ({ value }) =>
          getTextFromOptions(getPostThicknessOptions(holdownDesign, postBaseLumberSpecies), value),
      },
      {
        accessor: 'postWidth',
        Header: 'Post Width',
        Cell: ({ value }) =>
          getTextFromOptions(getPostWidthOptions(holdownDesign, postBaseLumberSpecies), value),
      },
      {
        accessor: 'postBaseType',
        Header: 'Post Base Type',
        Cell: ({ value }) => getTextFromOptions(postTypeData, value),
      },
      {
        accessor: 'postBaseFastenersType',
        Header: 'Post Fasteners',
        Cell: ({ value }) =>
          value
            .map((val) => getTextFromOptions(postBaseFastenersTypeOptions[postBaseSubType], val))
            .join(', '),
      },
    ],
  }

  return (
    <>
      <h4>Post & Post Base</h4>
      <TableViewPrint columns={columns[country]?.filter(Boolean)} data={[input]} sortBy={[]} />
    </>
  )
}

const PostBaseFoundationPrintTable = ({ input }) => {
  const { holdownDesign, foundationType } = input
  const columns = [
    {
      accessor: 'foundationType',
      Header: 'Foundation Type',
      Cell: ({ value }) => getTextFromOptions(foundationTypeData, value),
    },
    {
      accessor: 'minStemwallThickness',
      Header: 'Stemwall Thickness',
      Cell: ({ value }) =>
        getTextFromOptions(getMinStemwallThicknessOptions(holdownDesign, foundationType), value),
    },
  ]

  return (
    <>
      <h4>Foundation</h4>
      <TableViewPrint columns={columns} data={[input]} sortBy={[]} />
    </>
  )
}

const AnchorageDesignCriteriaPrintTable = ({ input }) => {
  const { materialType, holdownSubType, geometryType } = input

  const columns = [
    {
      accessor: 'materialType',
      Header: 'Base Material',
      Cell: ({
        value,
        row: {
          original: { holdownSubType },
        },
      }) => getTextFromOptions(materialTypeOptions[holdownSubType], value),
    },
    materialType === MATERIAL_TYPE.CONCRETE && {
      accessor: 'compressiveStrength',
      Header: 'Compressive Strength',
      Cell: ({
        value,
        row: {
          original: { holdownSubType },
        },
      }) => getTextFromOptions(compressiveStrengthOptions[holdownSubType], value),
    },
    holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR && {
      accessor: 'anchorageOption',
      Header: 'Anchor Type',
      Cell: ({
        value,
        row: {
          original: { materialType },
        },
      }) => getTextFromOptions(anchorageOptions[materialType], value),
    },
    holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP && {
      accessor: 'raisedFloor',
      Header: 'Raised Floor',
      Cell: ({ value }) => getTextFromOptions(raisedFloorData, value),
    },
    {
      accessor: 'geometryType',
      Header: 'Foundation Type',
      Cell: ({
        value,
        row: {
          original: { holdownSubType, materialType, anchorageOption },
        },
      }) =>
        getTextFromOptions(
          geometryTypeOptions[holdownSubType] ||
            geometryTypeOptions[materialType] ||
            geometryTypeOptions[anchorageOption],
          value,
        ),
    },
    geometryType != GEOMETRY_TYPE.ELEVATED_SLAB &&
      geometryType != GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING && {
        accessor: 'geometrySubType',
        Header: 'Application',
        Cell: ({
          value,
          row: {
            original: { geometryType, materialType, anchorageOption },
          },
        }) =>
          getTextFromOptions(
            geometrySubTypeOptions[materialType] ||
              geometrySubTypeOptions[anchorageOption][geometryType],
            value,
          ),
      },
  ]

  return (
    <>
      <h4>Anchorage</h4>
      <TableViewPrint columns={columns?.filter(Boolean)} data={[input]} sortBy={[]} />
    </>
  )
}

const GeometryTypeImages = ({ input }) => {
  const { holdownSubType, geometryType, materialType } = input
  const geometryTypeImageSrcs = geometryTypeImages(holdownSubType, materialType)[geometryType]

  if (!geometryTypeImageSrcs?.length) return null

  return (
    <>
      <h4>Foundation Type Details</h4>

      <div className="row">
        {geometryTypeImageSrcs.map((item) => (
          <div key={item.url} className="col-6" label="Foundation Type Details">
            <figure className="figure">
              <img className="illustration" src={item.url} alt={item.label} />
              <figcaption className="figcaption">{item.label}</figcaption>
            </figure>
          </div>
        ))}
      </div>
    </>
  )
}

export default PrintableInputOutput
